import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { GetInvestorValuationsRes } from '@dealstack/shared/lib/generated';
import { useEffect, useMemo, useState } from 'react';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable';
import { getInvestorHoldingsTableColumns } from './investorHoldingsTableColumns';
import type { HoldingsTableRow } from './utils/types';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState';
import { buildRows } from './utils/buildRows';
import { Table } from '@tanstack/react-table';
import { InvestorHoldingsDetailPanel } from '@dealstack/frontend/investor/pages/Valuations/InvestorHoldingsTable/components/InvestorHoldingsDetailPanel.tsx';

interface Props {
	data: GetInvestorValuationsRes[];
	showAllHoldingsMode: boolean;
}

export const InvestorHoldingsTable = ({ data, showAllHoldingsMode }: Props) => {
	const tInvestor = useTranslationInvestor();
	const [tableInstance, setTableInstance] = useState<Table<HoldingsTableRow> | null>(null);

	const isValueColumnVisible = data.some((investor) =>
		investor.valuations.some((v) => v.value !== null)
	);

	const rows = useMemo(() => buildRows(data), [data]);

	const columns = useMemo(
		() =>
			getInvestorHoldingsTableColumns({
				tInvestor,
				isValueColumnVisible,
				showAllHoldingsMode,
				securityValuationDate: data[0]?.securityValuationDate,
			}),
		[isValueColumnVisible, showAllHoldingsMode, data, tInvestor]
	);

	// Auto-expand loan rows once table is available
	useEffect(() => {
		if (!tableInstance) {
			return;
		}

		tableInstance.getRowModel().rows.forEach((row) => {
			if (row.original._loans && !row.getIsExpanded()) {
				row.toggleExpanded();
			}
		});
	}, [tableInstance, rows]);

	if (data.length === 0) {
		return <EmptyState />;
	}

	return (
		<div>
			<BasicMantineTable<HoldingsTableRow>
				columns={columns}
				data={rows}
				hideTopToolbar
				enableExpanding
				dataTestId={INVESTOR_TEST_ELEMENT_ID.INVESTOR_HOLDINGS_TABLE}
				getRowCanExpand={(row) =>
					!!row.original.isSubjectToVesting || !!row.original._loans
				}
				renderDetailPanel={({ row }) => (
					<InvestorHoldingsDetailPanel row={row} rows={rows} />
				)}
				mantineTableBodyRowProps={({ row, table }) => {
					if (!tableInstance) {
						setTableInstance(table);
					}

					const isLoanRow = !!row.original._loans;
					const nextRow = rows[row.index + 1];
					const isBeforeLoanRow = !!nextRow?._loans;
					// Remove borders between a holding row, its vesting panel, and the loan panel
					const removeBorderBottom = isLoanRow || isBeforeLoanRow || row.getIsExpanded();

					return {
						style: {
							height: 'unset',
							...(isLoanRow ? { display: 'none' } : undefined),
							...(removeBorderBottom ? { borderBottom: 'none' } : undefined),
						},
					};
				}}
				mantineDetailPanelProps={{
					style: {
						borderTop: 'none',
					},
				}}
			/>
		</div>
	);
};
